import { toLower } from 'lodash';
import moment from 'moment-timezone';
import { asTimeZone } from './dateFormatter';
import { isBooleanFalse, isBooleanTrue, isListEmpty, isListNotEmpty, isNullOrUndefined, isObjectEmpty, isStringNotEmpty } from './objectUtils';

// the rule a coupon fails on the plan alone; it is checked last, so a coupon failing it passes every other rule
export const APPLY_TO_BUNDLE_RULE = 'applyToBundle';

export const RECURRING_DISCOUNT_TARGETS = ['BUNDLE', 'MIXED_RECURRING', 'RECURRING'];

/**
 * Whether only the single-payment plan may be sold: to a friends and family account, and under a recurring coupon
 * that cannot discount a bundle. A recurring discount spread over a bundle breaks the per-month breakdown the plan
 * step shows ("from $X/mo", "$X first month, $Y after") - the whole discount lands on the bundle's first month,
 * which can then read as negative.
 */
export const isSinglePaymentPlanOnly = ({ coupon = null, isFriendsAndFamily = false } = {}) => {
	// getByCode returns the raw document, so applyToBundle is undefined on a coupon predating the field - a flag we
	// cannot confirm has to fail this check
	return isBooleanTrue(isFriendsAndFamily) || (RECURRING_DISCOUNT_TARGETS.includes(coupon?.discountTarget) && !isBooleanTrue(coupon?.applyToBundle));
};

// the cart item on a bundle the plan step will not offer, which has to move to the single-payment plan - left there,
// it bills a plan with no option selected
export const getCartItemOnUnofferedBundle = ({ cartItems = [], coupon = null, isFriendsAndFamily = false } = {}) => {
	if (isSinglePaymentPlanOnly({ coupon, isFriendsAndFamily }) === false) {
		return null;
	}

	return (isListNotEmpty(cartItems) ? cartItems : []).find(i => i?.bundleCount > 1) ?? null;
};

/**
 * Why a coupon cannot discount this cart, or null when it can. The rules run in the order the cart has always
 * applied them, and each failure is named by the coupon field it reads. A flag the coupon document predates
 * reads as unset and fails its check.
 *
 * @returns {string|null} the failed rule
 */
export const getCouponIneligibilityReason = ({ coupon = null, cartItems = [], variants = [], userId = null, email = null, now = moment.tz('US/Pacific') } = {}) => {
	if (isObjectEmpty(coupon)) {
		return null;
	}

	const items = isListNotEmpty(cartItems) ? cartItems : [];

	if (!(coupon.usageLimit === 0 || (coupon.usageLimit > 0 && coupon.usageCount < coupon.usageLimit))) {
		return 'usageLimit';
	}

	if (!(isNullOrUndefined(coupon.expiresAt) || asTimeZone(coupon.expiresAt, 'US/Pacific') > now)) {
		return 'expiresAt';
	}

	if (!(coupon.usageLimitPerUser === 0 || (isStringNotEmpty(userId) && coupon.usageLimitPerUser > (coupon.usedBy ?? []).filter(i => i === userId).length))) {
		return 'usageLimitPerUser';
	}

	if (!(isListEmpty(coupon.emailRestrictions) || (isStringNotEmpty(email) && coupon.emailRestrictions.some(i => toLower(i) === toLower(email))))) {
		return 'emailRestrictions';
	}

	const cartVariants = (variants ?? []).filter(i => items.some(ci => ci.variantId === i.id));
	if (!(isBooleanFalse(coupon.starterTitrationLevelOnly) || (isBooleanTrue(coupon.starterTitrationLevelOnly) && cartVariants.every(i => i.titrationLevel === 1 || isNullOrUndefined(i.titrationLevel))))) {
		return 'starterTitrationLevelOnly';
	}

	if (isListNotEmpty(coupon.excludedProductIds) && items.some(i => coupon.excludedProductIds.includes(i.id))) {
		return 'excludedProductIds';
	}

	if (isListNotEmpty(coupon.excludedVariantIds) && items.some(i => coupon.excludedVariantIds.includes(i.variantId))) {
		return 'excludedVariantIds';
	}

	const isBundle = items.some(i => i.bundleCount > 1);
	const isBundleAllowed = isBooleanTrue(coupon.applyToBundle) && (isListEmpty(coupon.excludedBundleCounts) || !items.some(i => coupon.excludedBundleCounts.includes(i.bundleCount)));
	if (!((isBooleanFalse(coupon.applyToBundle) && !isBundle) || isBundleAllowed)) {
		return APPLY_TO_BUNDLE_RULE;
	}

	return null;
};
